import { CHANGED_ELSEWHERE } from '../viewModel/refusals';
import { BasilCanonSet, BasilDigest } from './writeTypes';

/**
 * W3 — the staleness guard (spec section 5.3).
 *
 * The baseline is the read the script was decompiled from. Immediately before a batch the write
 * lane takes a fresh full read and compares, per node, the verifier's own projection line
 * `` `${id}:${stepIds}:${djb2(canon(branch))}` `` (`canon.ts:109-120`) in all three canons the
 * verdict itself compares: the V1 content canon, the V2 whole-writable canon and the layout canon.
 *
 * Two rules, and the second one is a fix:
 *
 * 1. The lines are the VERIFIER'S. They arrive over the worker boundary (`kind: 'canon'`) and are
 *    never recomputed on the main thread — the verifier's canonicalisation lives in the separately
 *    emitted chunk and nowhere else (ruling D2, gate check G8). With no canon the guard cannot prove
 *    the baseline holds, so it refuses: fail closed, never "probably fine".
 * 2. ANY drift refuses, on a node the plan touches and on one it does not. Drift on an untouched
 *    node used to be recorded and waved through; nothing then asked the operator to re-confirm, so
 *    a node someone else edited while the script was open was silently accepted and written over
 *    the top of. The after-read verdict would fail on it anyway (`verify.ts:311-329`) — after the
 *    write. Refusing here is the same answer, before it.
 *
 * Basil v0.2, P5: both drift refusals say the same sentence, because the writer's position is the
 * same in both — the episode moved, NOTHING was sent, and reopening Basil would discard whatever is
 * in the editor. The guard itself is unchanged: it runs before the first body, so "nothing was sent"
 * is a fact about this attempt and not a hope. The word is "reopening", never "reloading": the
 * recorded reopen loses the text, and a writer told to reload expects it to survive.
 *
 * `GET /v1/stories/:id/branches/changes?since=` is NEVER a substitute: step-only and answer-only
 * edit-context writes leave `Branches.updatedAt` untouched and are invisible to it (AD1:44-46,
 * AD2:11). E13 asserts the pipeline never reads a silent delta as "no change".
 */

export type StalenessReason = 'no-baseline' | 'no-canon' | 'touched-drift' | 'untouched-drift' | null;

export interface StalenessReport {
  /** true only when every node in the episode still matches its baseline line, in all three canons. */
  ok: boolean;
  reason: StalenessReason;
  message: string | null;
  /** node ids whose projection line moved and which the plan writes to. */
  touchedDrift: string[];
  /** node ids whose projection line moved elsewhere in the episode. */
  untouchedDrift: string[];
}

export interface StalenessInput {
  /** the canon of the read the script was decompiled from, taken at decompile time. */
  baseline: BasilCanonSet | null | undefined;
  /** the canon of the fresh read taken immediately before the batch. */
  fresh: BasilCanonSet | null | undefined;
  /** every node id the batch addresses, as strings. */
  touchedNodeIds: ReadonlySet<string>;
}

/** The id a projection line starts with: `41157055:237083780,…:1951054746` (`canon.ts:109-120`). */
export function nodeIdOfLine(line: string): string {
  const separator = line.indexOf(':');

  return separator === -1 ? line : line.slice(0, separator);
}

export function linesByNode(lines: readonly string[]): Map<string, string> {
  const byNode = new Map<string, string>();

  lines.forEach((line) => {
    byNode.set(nodeIdOfLine(line), line);
  });

  return byNode;
}

export function digestsEqual(left: BasilDigest | null | undefined, right: BasilDigest | null | undefined): boolean {
  if (!left || !right || left.length !== right.length) {
    return false;
  }

  return left.every((value, index) => value === right[index]);
}

/** The three canons a node can move in, named so a refusal can say which one it was. */
const CANONS: readonly (keyof BasilCanonSet)[] = ['v1', 'v2', 'layout'];

function driftedNodeIds(baseline: BasilCanonSet, fresh: BasilCanonSet): string[] {
  const drifted = new Set<string>();

  CANONS.forEach((canon) => {
    const before = linesByNode(baseline[canon].lines);
    const after = linesByNode(fresh[canon].lines);

    before.forEach((line, nodeId) => {
      if (after.get(nodeId) !== line) {
        drifted.add(nodeId);
      }
    });

    after.forEach((line, nodeId) => {
      if (!before.has(nodeId)) {
        drifted.add(nodeId);
      }
    });
  });

  return [...drifted];
}

/**
 * Compare the fresh read against the baseline the script was decompiled from.
 *
 * A node that disappeared, appeared, or whose line moved in any of the three canons counts as
 * drift, and any drift at all refuses.
 */
export function checkStaleness(input: StalenessInput): StalenessReport {
  const { baseline, fresh, touchedNodeIds } = input;

  if (!baseline) {
    return {
      ok: false,
      reason: 'no-baseline',
      message: 'Basil has nothing to compare this episode against, so nothing was sent. Reload it and try again.',
      touchedDrift: [],
      untouchedDrift: [],
    };
  }

  const missing = !fresh || CANONS.some((canon) => !baseline[canon] || !fresh[canon]
    || !Array.isArray(baseline[canon].lines) || !Array.isArray(fresh[canon].lines));

  if (missing || !fresh) {
    return {
      ok: false,
      reason: 'no-canon',
      message: "Basil's double-check couldn't read the episode to compare, so nothing was sent.",
      touchedDrift: [],
      untouchedDrift: [],
    };
  }

  const drifted = driftedNodeIds(baseline, fresh);
  const touchedDrift = drifted.filter((nodeId) => touchedNodeIds.has(nodeId));
  const untouchedDrift = drifted.filter((nodeId) => !touchedNodeIds.has(nodeId));

  if (touchedDrift.length > 0) {
    return {
      ok: false,
      reason: 'touched-drift',
      message: CHANGED_ELSEWHERE,
      touchedDrift,
      untouchedDrift,
    };
  }

  if (untouchedDrift.length > 0) {
    return {
      ok: false,
      reason: 'untouched-drift',
      message: CHANGED_ELSEWHERE,
      touchedDrift,
      untouchedDrift,
    };
  }

  return {
    ok: true, reason: null, message: null, touchedDrift, untouchedDrift,
  };
}
